/**
 * The shell's own account row: who is signed in and a direct way out --
 * no menu between the reader and either control (the owner: "a useless
 * component that hides the exit"). Clicking the name opens the account
 * settings dialog (`AccountSettings`), which is where the locale and the
 * theme live now; there is nothing left here for a menu to hold.
 */

import type { ReactNode } from 'react';
import { LogOut } from 'lucide-react';

import { IconHint } from '../hint';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../../ui/sidebar';
import { initials } from './initials';
import { sh } from './messages';

// On the rail the button is the avatar alone: the registry's `size="lg"` drops
// its padding there, and the name would still take width and push it off centre.
const RAIL_CENTRED = 'group-data-[collapsible=icon]:justify-center';

export function UserFooter(props: {
  name: string | null;
  onSignOut: () => void;
  /** Omitted by a plane with no account settings to open: `ops` pins its own
   *  locale at boot, so a language choice made there would appear to do
   *  nothing -- the name then renders as identity only, with no control to
   *  open. */
  onSettings?: () => void;
}): ReactNode {
  const identity = (
    <>
      <Avatar size="sm">
        <AvatarFallback aria-hidden>{initials(props.name)}</AvatarFallback>
      </Avatar>
      {props.name !== null && (
        <span className="truncate group-data-[collapsible=icon]:hidden">{props.name}</span>
      )}
    </>
  );

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        {props.onSettings === undefined ? (
          <SidebarMenuButton asChild size="lg" className={RAIL_CENTRED}>
            <span>{identity}</span>
          </SidebarMenuButton>
        ) : (
          <SidebarMenuButton
            size="lg"
            className={RAIL_CENTRED}
            onClick={props.onSettings}
            aria-label={
              props.name === null
                ? sh('account.settings')
                : sh('account.trigger', { name: props.name })
            }
          >
            {identity}
          </SidebarMenuButton>
        )}
        {/* Hidden on the icon rail by the registry's own menu-action rule:
         *  the rail has no room beside the avatar, and the settings dialog
         *  the avatar opens carries its own sign-out. */}
        <IconHint label={sh('account.signOut')}>
          <SidebarMenuAction onClick={() => props.onSignOut()}>
            <LogOut aria-hidden />
          </SidebarMenuAction>
        </IconHint>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
